import { useToast } from "@chakra-ui/react";
import { useContext } from "react";
import { useIntl } from "react-intl";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { getErrorString, getErrors } from "@nens/ranalib";
import {
  CROSS_TENANT_INVITATION_SESSION_KEY,
  INVITATION_ID_KEY,
  TenantContext,
  chosenIdentityProviderKey,
} from "@nens/ranalib";
import { useOnlyOnce } from "@nens/react/hooks";

// Hook to accept an invitation if there is an invitation id in session storage
// An invitation is accepted once the dashboard is loaded, so this hook
// should be called inside the TenantDetails component

export function useInvitationAccept() {
  const tenantContext = useContext(TenantContext);
  const toast = useToast();
  const intl = useIntl();

  // Accept the invitation if there is an invitation id in session storage
  useOnlyOnce(() => {
    const invitationId = window.sessionStorage.getItem(INVITATION_ID_KEY);

    if (!invitationId || tenantContext.isPublic) return;

    const acceptInvitation = async () => {
      // Remove the invitation id from session storage
      window.sessionStorage.removeItem(INVITATION_ID_KEY);

      // If we come here with an invitation id, then the chosen identity provider
      // has been removed from the local storage. We need to set it again — but only
      // if this is NOT a cross-tenant invitation (where the user authenticated via
      // an IdP that is not configured for this tenant). Storing a foreign IdP would
      // cause infinite redirect loops on future visits.
      const isCrossTenantInvitation = window.sessionStorage.getItem(
        CROSS_TENANT_INVITATION_SESSION_KEY
      );
      window.sessionStorage.removeItem(CROSS_TENANT_INVITATION_SESSION_KEY);

      if (!isCrossTenantInvitation) {
        window.localStorage.setItem(
          chosenIdentityProviderKey(tenantContext.tenant),
          tenantContext.identityProvider
        );
      }

      try {
        await getRanaApiV1().InvitationsApi.invitationAccept({
          invitationId,
        });
        toast({
          title: intl.formatMessage({
            id: "invitation.accepted",
            defaultMessage: "Invitation accepted",
          }),
          description: intl.formatMessage({
            id: "invitation.accepted_description",
            defaultMessage: "You have accepted the invitation",
          }),
          status: "success",
        });
      } catch (e) {
        if (!e) return;
        const errors = await getErrors(e, intl);
        toast({
          title: intl.formatMessage({
            id: "invitation.error",
            defaultMessage: "Invitation error",
          }),
          description: getErrorString(errors),
          status: "error",
        });
      }
    };

    acceptInvitation();
  });

  return null;
}
